import type { AmazonSalesResponse } from "reedy-data/api";
import type { FormatMeta } from "./mapSalesResponse";

// Per-format metrics on the datum so the tooltip never re-derives; null is a real gap, not zero.
export interface AmazonFormatDetail {
  units: number | null;
  revenue: number | null;
  conversionRate: number | null;
  salesRank: number | null;
  salesRankDate: string | null;
}

// Per-format units are top-level dataKeys for recharts stacks; aggregates stay on the datum for overlay lines.
export interface AmazonFormatDatum {
  date: string;
  conversionRate: number | null;
  salesRank: number | null;
  detail: Record<string, AmazonFormatDetail>;
  [seriesKey: string]:
    | number
    | string
    | null
    | Record<string, AmazonFormatDetail>;
}

export interface AmazonFormatViewModel {
  data: AmazonFormatDatum[];
  formats: FormatMeta[];
}

// Format order comes from the bundle context, not the response; missing ISBNs zero-fill stack height, detail keeps null for tooltip gaps.
export const mapAmazonFormatResponse = (
  sales: Pick<AmazonSalesResponse, "buckets">,
  formats: FormatMeta[],
): AmazonFormatViewModel => {
  const data: AmazonFormatDatum[] = sales.buckets.map((bucket) => {
    const detail: Record<string, AmazonFormatDetail> = {};
    const datum: AmazonFormatDatum = {
      date: bucket.date,
      conversionRate: bucket.conversionRate,
      salesRank: bucket.salesRank,
      detail,
    };
    formats.forEach((format) => {
      const member = bucket.byIsbn?.[format.isbn];
      datum[format.isbn] = member?.units ?? 0;
      detail[format.isbn] = {
        units: member?.units ?? null,
        revenue: member?.revenue ?? null,
        conversionRate: member?.conversionRate ?? null,
        salesRank: member?.salesRank ?? null,
        salesRankDate: member?.salesRankDate ?? null,
      };
    });
    return datum;
  });

  return { data, formats };
};
